import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
    Box,
    Typography,
    IconButton,
    FormControl,
    AppBar,
    Toolbar,
    MenuItem,
    Select,
} from "@mui/material";
import VideoCallIcon from "@mui/icons-material/VideoCall";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useAuthStore } from "../../store/useAuthStore";
import { Post } from "../../http/httpVerbs";
import { Loader } from "../../components/ui/Loader";
import { UploadProgress } from "../../components/ui/UploadProgress";
import { useErrorStore } from "../../store/useErrorStore";
import { ButtonFL } from "../../components/ui/ButtonFL";
import { apiGetComunidade } from "../../services/apiGetComunidade";
import { apiGetDesafios } from "../../services/apiGetDesafios";
import { apiPostDesafioCreator } from "../../services/apiPostDesafioCreator";
import { apiGetPostThemes } from "../../services/apiGetPostThemes";
import { apiGetPostFormats } from "../../services/apiGetPostFormats";
import { AuthorizationModal } from "../../components/AuthorizationModal";
import { clickSignTypes } from "../../constants/clicksignTypes";
import { getImageAuthorization } from "../../services/apiGetClicksign";
import { TextEditor } from "../../components/ui/TextEditor";
import { useFormattedText } from "../../hooks/useFormattedText";
import { ConfirmPostModal } from "../../components/ui/ConfirmPostModal";
import { creatorIsAdmin } from "../../utils/adminManager";
import { WIZARD_COMMUNITY_ID } from "../../constants/wizard";

export const NewPost = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const isAdmin = creatorIsAdmin();

    const { state } = location;

    const showError = useErrorStore((state) => state.showError);
    const creator = useAuthStore((state) => state.creator);

    const [isLoading, setIsLoading] = useState(false);
    const [isPublishing, setIsPublishing] = useState(false);
    const [isPublishComplete, setIsPublishComplete] = useState(false);
    const [inputValue, setInputValue] = useState("");
    const [attachedFile, setAttachedFile] = useState(null);
    const [selectedTheme, setSelectedTheme] = useState('');
    const [selectedFormat, setSelectedFormat] = useState('');
    const [themeError, setThemeError] = useState(isAdmin ? "" : "Selecione um tema");
    const [formatError, setFormatError] = useState(isAdmin ? "" : "Selecione um formato");
    const [comunidade, setComunidade] = useState([]);
    const [themes, setThemes] = useState([]);
    const [formats, setFormats] = useState([]);
    const [desafios, setDesafios] = useState([]);
    const [selectedDesafioId, setSelectedDesafioId] = useState("");
    const [filePreviewUrl, setFilePreviewUrl] = useState(null);
    const [modalOpen, setModalOpen] = useState(!isAdmin);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [statusAuthorization, setStatusAuthorization] = useState(isAdmin ? "close" : "");

    const fileInputRef = useRef(null);

    const { sanitizeContent } = useFormattedText();

    const handleInputChange = (value) => {
        setInputValue(value);
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith("video/")) {
            alert("Por favor, selecione um arquivo de vídeo válido");
            return;
        }

        if (file.size > 100 * 1024 * 1024) {
            alert("O arquivo é muito grande. O tamanho máximo é 100MB");
            return;
        }

        setAttachedFile(file);
        setFilePreviewUrl(URL.createObjectURL(file));
    };

    const handleRemoveFile = () => {
        if (filePreviewUrl) {
            URL.revokeObjectURL(filePreviewUrl);
        }
        setAttachedFile(null);
        setFilePreviewUrl(null);
    };

    const validateForm = () => {
        if (isAdmin) {
            return true;
        }

        if (!attachedFile) {
            alert("É obrigatório enviar um vídeo!");
            return false;
        }

        if (!selectedTheme) {
            alert("Selecione um tema");
            return false;
        }

        if (!selectedFormat) {
            alert("Selecione um formato");
            return false;
        }

        return true;
    };

    const handleSubmit = async () => {
        if (statusAuthorization !== "close") {
            navigate(`/comunidade/${WIZARD_COMMUNITY_ID}`);
            return;
        }

        if (!validateForm()) {
            return;
        }

        const formData = new FormData();

        formData.append("comunidade", Number(WIZARD_COMMUNITY_ID));
        formData.append("texto", sanitizeContent(inputValue));
        formData.append("creator", creator.id);
        formData.append("theme", selectedTheme);
        formData.append("format", selectedFormat);

        if (attachedFile) formData.append("file", attachedFile);

        try {
            setIsLoading(true);
            setIsPublishing(Boolean(attachedFile));

            if (selectedDesafioId) {
                const desafioCreatorResponse = await apiPostDesafioCreator({
                    desafio: selectedDesafioId,
                    creator: creator.id,
                    post: false,
                });

                formData.append("desafiocreator", desafioCreatorResponse.id);
            }

            await Post(`${process.env.REACT_APP_API_URL}/posts/`, formData);

            if (attachedFile) {
                // Let the progress bar reach 100% before leaving the page
                setIsPublishComplete(true);
                await new Promise((resolve) => setTimeout(resolve, 600));
            }

            setInputValue("");
            setAttachedFile(null);
            setSelectedTheme("");
            setSelectedFormat("");

            navigate(`/post-success/${WIZARD_COMMUNITY_ID}`);
        } catch (error) {
            showError(error);
        } finally {
            setIsLoading(false);
            setIsPublishing(false);
            setIsPublishComplete(false);
        }
    };

    const showHideModal = (status) => {
        setStatusAuthorization(status);

        if (status === "close") {
            setModalOpen(false);
            setConfirmModalOpen(true);
        }
    };

    const getAuthorizationStatus = async (notification) => {
        try {
            const response = await getImageAuthorization(clickSignTypes.IMAGE_AUTHORIZATION, creator.id, notification);
            showHideModal(response.status);
            return response;
        } catch (error) {
            console.log("AutorizationModal error", error);
            showError(error);
        }
    };

    const handleSignDocument = async () => {
        try {
            const response = await getAuthorizationStatus();
            if (response?.signUrl) {
                window.open(response.signUrl, "_blank");
            }
        } catch (error) {
            console.error("Erro ao abrir documento para assinatura:", error);
            showError("Erro ao abrir documento para assinatura. Tente novamente.");
        }
    };

    const updateComponentState = async () => {
        try {
            setIsLoading(true);

            const response = await getImageAuthorization(clickSignTypes.IMAGE_AUTHORIZATION, creator.id);
            showHideModal(response.status);
        } catch (error) {
            console.error("Erro ao atualizar o estado do componente:", error);
            showError("Erro ao atualizar. Tente novamente.");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!isAdmin) getAuthorizationStatus();
    }, []);

    const fetchAllData = useCallback(async () => {
        try {
            setIsLoading(true);
            const [dataComunidade, dataDesafios, dataThemes, dataFormats] = await Promise.all([
                apiGetComunidade(WIZARD_COMMUNITY_ID),
                apiGetDesafios({ comunidade: WIZARD_COMMUNITY_ID }),
                apiGetPostThemes(),
                apiGetPostFormats(),
            ]);

            if (!dataComunidade) {
                showError("Comunidade não encontrada");
                navigate("/");
                return;
            }

            setComunidade(dataComunidade);
            setDesafios(dataDesafios);
            setThemes(dataThemes);
            setFormats(dataFormats);

            if (state?.desafio) {
                setSelectedDesafioId(state.desafio.id);
            }
        } catch (error) {
            showError(error);
        } finally {
            setIsLoading(false);
        }
    }, [state?.desafio, showError]);

    useEffect(() => {
        fetchAllData();
    }, [fetchAllData]);

    if (isPublishing) return <UploadProgress fileSize={attachedFile?.size} completed={isPublishComplete} />;

    if (isLoading) return <Loader />;

    return (
        <>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    backgroundColor: "var(--grey-100-color)",
                    color: "var(--grey-800-color)",
                    height: "100vh",
                    width: "100vw",
                    position: "fixed",
                    top: 0,
                    left: 0,
                    zIndex: 1200,
                }}
            >
                <AppBar
                    position="fixed"
                    sx={{
                        backgroundColor: "var(--primary-color)",
                        top: 0,
                        left: 0,
                        right: 0,
                        zIndex: 1200,
                    }}
                >
                    <Toolbar sx={{ minHeight: { xs: "56px" } }}>
                        <IconButton
                            edge="start"
                            color="inherit"
                            onClick={() => navigate(`/comunidade/${WIZARD_COMMUNITY_ID}`)}
                            sx={{ mr: 2 }}
                        >
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="subtitle1" component="div" sx={{ flexGrow: 1 }}>
                            Nova postagem
                        </Typography>
                    </Toolbar>
                </AppBar>

                <Box
                    sx={{
                        flex: 1,
                        overflow: "auto",
                        p: 2,
                        pt: "64px",
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                    }}
                >
                    <VideoUpload
                        fileInputRef={fileInputRef}
                        filePreviewUrl={filePreviewUrl}
                        handleFileUpload={handleFileUpload}
                        handleRemoveFile={handleRemoveFile}
                    />

                    <Box>
                        <Typography
                            variant="subtitle2"
                            color="var(--grey-800-color)"
                            sx={{ fontWeight: 600, mb: 1 }}
                        >
                            Descrição
                        </Typography>

                        <Box sx={{ p: 2, bgcolor: "background.paper", borderRadius: 1 }}>
                            <TextEditor editorContent={inputValue} setEditorContent={handleInputChange} height="150px" />
                        </Box>
                    </Box>

                    <Box>
                        <Box>
                            <Typography
                                variant="subtitle2"
                                color="var(--grey-800-color)"
                                sx={{ fontWeight: 600, mb: 1 }}
                            >
                                Desafios
                            </Typography>

                            <FormControl fullWidth>
                                <Select
                                    name="desafio"
                                    value={selectedDesafioId}
                                    onChange={(e) => setSelectedDesafioId(e.target.value)}
                                    displayEmpty
                                    inputProps={{ "aria-label": "Selecione um desafio" }}
                                    size="small"
                                    sx={{ bgcolor: "white" }}
                                >
                                    <MenuItem value="">
                                        <em>Selecione um desafio (opcional)</em>
                                    </MenuItem>
                                    {desafios.map((desafio) => (
                                        <MenuItem
                                            key={desafio.id}
                                            value={desafio.id}
                                            selected={state?.desafio?.id === desafio.id}
                                        >
                                            {desafio.titulo}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" color="var(--grey-800-color)" sx={{ fontWeight: 600, mb: 1 }}>
                                Tema {isAdmin ? "- Você é admin, esse campo não é obrigatório" : "*"}
                            </Typography>

                            <FormControl fullWidth>
                                <Select
                                    name="tema"
                                    value={selectedTheme}
                                    onChange={(e) => {
                                        setSelectedTheme(e.target.value);
                                        setThemeError("");
                                    }}
                                    displayEmpty
                                    inputProps={{ "aria-label": "Selecione um tema" }}
                                    size="small"
                                    sx={{ bgcolor: "white" }}
                                    error={!!themeError}
                                >
                                    <MenuItem value="">
                                        <em>Selecione um tema</em>
                                    </MenuItem>
                                    {themes?.map((theme) => (
                                        <MenuItem key={theme} value={theme}>
                                            {theme}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {themeError && (
                                    <Typography variant="caption" color="error" sx={{ pl: 1, pt: 1, display: "block" }}>
                                        {themeError}
                                    </Typography>
                                )}
                            </FormControl>
                        </Box>

                        <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" color="var(--grey-800-color)" sx={{ fontWeight: 600, mb: 1 }}>
                                Formato {isAdmin ? "- Você é admin, esse campo não é obrigatório" : "*"}
                            </Typography>

                            <FormControl fullWidth>
                                <Select
                                    name="formato"
                                    value={selectedFormat}
                                    onChange={(e) => {
                                        setSelectedFormat(e.target.value);
                                        setFormatError("");
                                    }}
                                    displayEmpty
                                    inputProps={{ "aria-label": "Selecione um formato" }}
                                    size="small"
                                    sx={{ bgcolor: "white", mb: 8 }}
                                    error={!!formatError}
                                >
                                    <MenuItem value="">
                                        <em>Selecione um formato</em>
                                    </MenuItem>
                                    {formats?.map((format) => (
                                        <MenuItem key={format} value={format}>
                                            {format}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {formatError && (
                                    <Typography variant="caption" color="error" sx={{ pl: 1, pt: 1, display: "block" }}>
                                        {formatError}
                                    </Typography>
                                )}
                            </FormControl>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            position: "fixed",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            p: 2,
                            backgroundColor: "var(--white-color)",
                            borderTop: "1px solid var(--grey-200-color)",
                            boxShadow: "0px -4px 6px -1px rgba(0, 0, 0, 0.1), 0px -2px 4px -1px rgba(0, 0, 0, 0.06)",
                            zIndex: 2,
                        }}
                    >
                        <ButtonFL textKey="Publicar" onClick={handleSubmit} bgcolor="var(--botao-acao)" fullWidth />
                    </Box>
                </Box>
            </Box>

            {modalOpen && (
                <AuthorizationModal
                    open={modalOpen}
                    onClose={() => setModalOpen(false)}
                    getAuthorizationStatus={getAuthorizationStatus}
                    updateComponentState={updateComponentState}
                    onSignDocument={handleSignDocument}
                />
            )}

            <ConfirmPostModal
                open={!modalOpen && confirmModalOpen && statusAuthorization === "close"}
                onConfirm={() => setConfirmModalOpen(false)}
            />
        </>
    );
};

const VideoUpload = ({
    fileInputRef,
    filePreviewUrl,
    handleFileUpload,
    handleRemoveFile,
}) => {
    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "center",
                width: "100%",
            }}
        >
            <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleFileUpload}
                accept="video/*"
            />
            {filePreviewUrl ? (
                <Box
                    sx={{
                        width: 280,
                        aspectRatio: "9/16",
                        position: "relative",
                        borderRadius: 2,
                        overflow: "hidden",
                    }}
                >
                    <video
                        src={filePreviewUrl}
                        style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                        }}
                        controls
                    />
                    <IconButton
                        onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveFile();
                        }}
                        sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            backgroundColor: "rgba(0,0,0,0.5)",
                            "&:hover": {
                                backgroundColor: "rgba(0,0,0,0.7)",
                            },
                        }}
                    >
                        <CloseIcon sx={{ color: "white" }} />
                    </IconButton>
                </Box>
            ) : (
                <Box
                    onClick={() => fileInputRef.current.click()}
                    sx={{
                        width: 280,
                        aspectRatio: "9/16",
                        backgroundColor: "var(--white-color)",
                        border: "1px solid var(--grey-200-color)",
                        borderRadius: 2,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        transition: "all 0.2s",
                        "&:hover": {
                            backgroundColor: "var(--grey-50-color)",
                            transform: "translateY(-1px)",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.05), 0 2px 4px rgba(0,0,0,0.06)",
                        },
                    }}
                >
                    <VideoCallIcon sx={{ fontSize: 48, color: "var(--grey-800-color)", mb: 1 }} />
                    <Typography variant="body2" color="var(--grey-800-color)" align="center" sx={{ px: 2 }}>
                        Clique aqui para escolher o vídeo
                    </Typography>
                </Box>
            )}
        </Box>
    );
};
